if (!customElements.get("td-megamenu")) {
  class TDMegamenu extends HTMLElement {
    constructor() {
      super();
      
      // Configuration
      this.DEBUG = false;
      this.HOVER_DELAY = parseInt(this.dataset.hoverDelay) || 0;
      this.CLOSE_DELAY = parseInt(this.dataset.closeDelay) || 0;
      
      this.CLASSES = {
        ACTIVE: "nav-hover",
        FOCUS: "nav-focus", 
        OUTSIDE: "nav-outside",
        HAS_DROPDOWN: "site-nav--has-dropdown",
        LINK: "site-nav__link",
      };

      this.SELECTORS = {
        ACTIVE: "." + this.CLASSES.ACTIVE,
        HAS_DROPDOWN: "." + this.CLASSES.HAS_DROPDOWN,
        DROPDOWN: "[data-meganav-dropdown]",
        LINK: "a",
        PARENT_LINK: '[data-meganav-type="parent"]',
        CHILD_LINK: '[data-meganav-type="child"]',
      };

      // State
      this.timer = null;
      this.closeTimer = null;
      this.navigation = null;
      this.allLinks = [];
      this.parents = [];
      this.childLinks = [];
      this.topLevel = [];
      this.dropdowns = [];
      this.subMenuLinks = [];

      // Bind methods to preserve context
      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.handleChildTouch = this.handleChildTouch.bind(this);
      this.handleFocus = this.handleFocus.bind(this);
      this.handleBlur = this.handleBlur.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleTouchOutside = this.handleTouchOutside.bind(this);
      this.handleDropdownOffset = this.handleDropdownOffset.bind(this);
    }

    /**
     * Web Component Lifecycle - Connected
     */
    connectedCallback() {
      // Get navigation element
      this.navigation = this.querySelector('#AccessibleNav') || this;

      if (!this.navigation) {
        this.log('Navigation not found', 'warn');
        return;
      }

      // Cache DOM elements
      this.cacheElements();

      // Bind events
      this.bindEvents();

      // Handle viewport changes
      window.addEventListener('resize', this.handleDropdownOffset);

      // Stabilize nav link widths to prevent jitter on font-weight hover change
      this.stabilizeNavLinkWidths();
    }

    /**
     * Measure each nav link's bold width and lock it via min-width
     * to prevent layout jitter when font-weight changes on hover.
     */
    stabilizeNavLinkWidths() {
      const lock = () => {
        const links = this.querySelectorAll('.td-nav__link');
        for (let i = 0; i < links.length; i++) {
          const link = links[i];
          link.style.transition = 'none';
          link.style.fontWeight = '700';
          void link.offsetWidth;
          const w = Math.ceil(link.getBoundingClientRect().width) + 1;
          link.style.fontWeight = '';
          link.style.minWidth = w + 'px';
          void link.offsetWidth;
          link.style.transition = '';
        }
      };

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(lock);
      } else {
        lock();
      }
    }

    /**
     * Web Component Lifecycle - Disconnected
     */
    disconnectedCallback() {
      this.cleanup();
    }

    /**
     * Cache DOM elements for performance
     */
    cacheElements() {
      this.allLinks = this.navigation.querySelectorAll(this.SELECTORS.LINK);
      this.parents = this.navigation.querySelectorAll(this.SELECTORS.HAS_DROPDOWN);
      this.childLinks = this.navigation.querySelectorAll(this.SELECTORS.CHILD_LINK);
      this.topLevel = Array.from(this.parents).map(parent => 
        parent.querySelector('a:first-child')
      ).filter(Boolean);
      this.dropdowns = this.navigation.querySelectorAll(this.SELECTORS.DROPDOWN);
      this.subMenuLinks = this.navigation.querySelectorAll(
        this.SELECTORS.DROPDOWN + ' ' + this.SELECTORS.LINK
      );
    }

    /**
     * Bind event listeners
     */
    bindEvents() {
      // Mouse and touch events for dropdown parents
      this.parents.forEach(parent => {
        parent.addEventListener('mouseenter', this.handleMouseEnter);
        parent.addEventListener('touchstart', this.handleMouseEnter);
        parent.addEventListener('mouseleave', this.handleMouseLeave);
      });

      // Touch events for child links
      this.childLinks.forEach(childLink => {
        childLink.addEventListener('touchstart', this.handleChildTouch);
      });

      // Focus events for all links
      this.allLinks.forEach(link => {
        link.addEventListener('focus', this.handleFocus);
        link.addEventListener('blur', this.handleBlur);
      });

      // Focus detection for entire navigation container
      this.navigation.addEventListener('focusin', this.handleFocusIn);
      this.navigation.addEventListener('focusout', this.handleFocusOut);
    }

    /**
     * Handle mouse enter and touch start events
     */
    handleMouseEnter(evt) {
      const el = evt.currentTarget;
      const evtType = evt.type;
      const activeDropdowns = this.navigation.querySelectorAll(this.SELECTORS.ACTIVE);

      if (!el.classList.contains(this.CLASSES.ACTIVE)) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
      }

      // Clear any pending close timer
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }

      // Close other active dropdowns before opening a new one
      if (activeDropdowns.length > 0) {
        const isDifferentDropdown = !Array.from(activeDropdowns).includes(el);
        
        if (isDifferentDropdown) {
          this.hideDropdowns();
        }
      }

      // 500ms delay
      this.timer = setTimeout(() => {
        this.showDropdown(el);
      }, this.HOVER_DELAY);
    }

    /**
     * Handle mouse leave events
     */
    handleMouseLeave(evt) {
      clearTimeout(this.timer);
      
      // Capture the element reference before the timeout
      const targetElement = evt.currentTarget;
      
      // Add configurable delay before closing
      this.closeTimer = setTimeout(() => {
        this.hideDropdown(targetElement);
        this.closeTimer = null;
      }, this.CLOSE_DELAY);
    }

    /**
     * Handle child link touch events
     */
    handleChildTouch(evt) {
      evt.stopImmediatePropagation();
    }

    /**
     * Handle focus events
     */
    handleFocus(evt) {
      const el = evt.currentTarget;
      const previousItem = el.parentElement.previousElementSibling;
      const newFocus = el.closest(this.SELECTORS.HAS_DROPDOWN)?.querySelector(':scope > a');

      // Set tabindex on all links
      this.allLinks.forEach(link => link.setAttribute('tabindex', ''));

      if (previousItem?.classList.contains(this.CLASSES.hasDropdown)) {
        const prevDropdownLinks = previousItem.querySelectorAll(this.SELECTORS.DROPDOWN + ' a');
        prevDropdownLinks.forEach(link => link.setAttribute('tabindex', '-1'));
      }

      if (newFocus) {
        this.addFocus(newFocus);
        // Show the dropdown when focusing on a parent link
        const parentElement = newFocus.closest(this.SELECTORS.HAS_DROPDOWN);
        if (parentElement) {
          this.showDropdown(parentElement);
        }
      }
    }

    /**
     * Handle blur events
     */
    handleBlur(evt) {
      this.removeFocus(this.topLevel);
    }

    /**
     * Handle focusin events - focus enters the navigation
     */
    handleFocusIn(evt) {
      // Focus has entered the navigation area
      this.log('Focus entered navigation');
    }

    /**
     * Handle focusout events - focus leaves the navigation
     * Added to help with tab navigation
     */
    handleFocusOut(evt) {
      const relatedTarget = evt.relatedTarget;
      
      
      // Check if focus is moving to an element outside the navigation
      if (!this.navigation.contains(relatedTarget)) {
        setTimeout(() => {
          this.hideDropdowns();
        }, 100);
        return;
      }
      
      // Focus is moving within navigation - check if it's moving to a child of active dropdown
      const activeDropdowns = this.navigation.querySelectorAll(this.SELECTORS.ACTIVE);
      let focusMovingToActiveChild = false;
      
      activeDropdowns.forEach(dropdown => {
        if (dropdown.contains(relatedTarget)) {
          focusMovingToActiveChild = true;
        }
      });
      
      // If focus is not moving to a child of any active dropdown, close all dropdowns
      if (!focusMovingToActiveChild && activeDropdowns.length > 0) {
        setTimeout(() => {
          this.hideDropdowns();
        }, 100);
      }
    }

    /**
     * Show dropdown
     */
    showDropdown(el) {
      const toplevel = el.querySelector(':scope > a');

      if (toplevel) {
        toplevel.setAttribute('aria-expanded', 'true');
      }

      el.classList.add(this.CLASSES.ACTIVE);
    }

    /**
     * Hide dropdown
     */
    hideDropdown(el) {
      const toplevel = el.querySelector(':scope > a');

      if (toplevel) {
        toplevel.setAttribute('aria-expanded', 'false');
      }

      el.classList.remove(this.CLASSES.ACTIVE);
      document.body.removeEventListener('touchstart', this.handleTouchOutside);
    }

    /**
     * Hide all dropdowns
     */
    hideDropdowns() {
      // Clear any pending timers
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
      
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
      
      const activeDropdowns = this.navigation.querySelectorAll(this.SELECTORS.ACTIVE);
      activeDropdowns.forEach(dropdown => {
        this.hideDropdown(dropdown);
      });
    }

    /**
     * Handle touch outside navigation
     */
    handleTouchOutside(evt) {
      if (!this.navigation.contains(evt.target)) {
        this.hideDropdowns();
      }
    }

    /**
     * Add focus state
     */
    addFocus(el) {
      el.classList.add(this.CLASSES.FOCUS);
      
      if (el.hasAttribute('aria-expanded')) {
        el.setAttribute('aria-expanded', 'true');
      }
    }

    /**
     * Remove focus state
     */
    removeFocus(el) {
      if (Array.isArray(el)) {
        el.forEach(element => {
          element.classList.remove(this.CLASSES.FOCUS);
          if (element.hasAttribute('aria-expanded')) {
            element.setAttribute('aria-expanded', 'false');
          }
        });
      } else {
        el.classList.remove(this.CLASSES.FOCUS);
        if (el.hasAttribute('aria-expanded')) {
          el.setAttribute('aria-expanded', 'false');
        }
      }
      
      this.subMenuLinks.forEach(link => {
        link.setAttribute('tabindex', '-1');
      });
    }

    /**
     * Handle dropdown offset for viewport positioning
     */
    handleDropdownOffset() {
      const viewportSize = window.innerWidth;
      const activeDropdowns = this.navigation.querySelectorAll(this.SELECTORS.ACTIVE);
      
      activeDropdowns.forEach(dropdown => {
        dropdown.classList.remove(this.CLASSES.OUTSIDE);
        const rect = dropdown.getBoundingClientRect();
        const dropdownOffset = rect.left + rect.width;
        
        if (dropdownOffset > viewportSize) {
          dropdown.classList.add(this.CLASSES.OUTSIDE);
        }
      });
    }

    /**
     * Cleanup event listeners
     */
    cleanup() {
      this.log('Cleaning up TD Megamenu component');
      
      // Clear timers
      if (this.timer) {
        clearTimeout(this.timer);
      }
      
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
      }

      // Remove event listeners
      window.removeEventListener('resize', this.handleDropdownOffset);
      document.body.removeEventListener('touchstart', this.handleTouchOutside);
      
      // Remove focus detection listeners
      if (this.navigation) {
        this.navigation.removeEventListener('focusin', this.handleFocusIn);
        this.navigation.removeEventListener('focusout', this.handleFocusOut);
      }
    }

    /**
     * Logging utility
     */
    log(content, type = 'log') {
      if (this.DEBUG && console[type]) {
        console[type](`[TD-Megamenu] ${content}`);
      }
    }

    /**
     * Refresh component (useful for dynamic content)
     */
    refresh() {
      this.cleanup();
      this.cacheElements();
      this.bindEvents();
    }
  }

  customElements.define("td-megamenu", TDMegamenu);
}